import { FC, useMemo, useState } from 'react';
import { Col, Dropdown, MenuProps, Row } from 'antd';
import { TbDevices, TbMoon, TbPalette, TbSun } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { ThemeSelectionOption } from '@/hooks/useThemeConfig';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';

// import { useAntDesignThemeuseAntDesignTheme } from '@/hooks';
// import { isSystemDefaultThemeDarkMode } from '@/utilities';

// interface ThemeDropdownProps {}

const renderThemeIcon = (type: ThemeSelectionOption['type']) => {
    const iconStyle = { marginRight: '0.375rem', verticalAlign: '-0.125rem' };

    if (type === 'workspace') {
        return <TbPalette style={iconStyle} />;
    }

    if (type === 'dark') {
        return <TbMoon style={iconStyle} />;
    }

    if (type === 'light') {
        return <TbSun style={iconStyle} />;
    }

    return <TbDevices style={iconStyle} />;
};

const renderThemeLabel = ({ label, type }: ThemeSelectionOption) => (
    <>
        {renderThemeIcon(type)}
        {label}
    </>
);

const ThemeDropdown: FC = ({}): JSX.Element => {
    const [open, setOpen] = useState(false);

    const { token, themeName, themeOptions, setTheme } = useAntDesignTheme();

    const themeMenuItems = useMemo<MenuProps['items']>(() => {
        const workspaceThemes = themeOptions.filter(({ type }) => type === 'workspace');
        const standardThemes = themeOptions.filter(({ type }) => type !== 'workspace');

        return [
            ...(workspaceThemes.length
                ? [
                      {
                          key: 'workspace-themes',
                          type: 'group' as const,
                          label: <small style={{ color: token.colorTextTertiary }}>Workspace</small>,
                          children: workspaceThemes.map((option) => ({
                              disabled: option.disabled,
                              key: option.name,
                              label: renderThemeLabel(option),
                          })),
                      },
                      { type: 'divider' as const },
                  ]
                : []),
            {
                key: 'standard-themes',
                type: 'group',
                label: <small style={{ color: token.colorTextTertiary }}>Appearance</small>,
                children: standardThemes.map((option) => ({
                    disabled: option.disabled,
                    key: option.name,
                    label: renderThemeLabel(option),
                })),
            },
        ];
    }, [themeOptions, token.colorTextTertiary]);

    const handleThemeMenuClick: MenuProps['onClick'] = ({ key }) => {
        setOpen(false);

        if (key === themeName) {
            return;
        }

        window.setTimeout(() => {
            setTheme(key);
        }, 0);
    };
    const activeTheme = themeOptions.find(({ name }) => name === themeName);

    return (
        <Dropdown
            trigger={['click']}
            open={open}
            onOpenChange={setOpen}
            destroyOnHidden
            popupRender={() => (
                <DropdownMenuPanel
                    {...{
                        items: themeMenuItems,
                        selectedKeys: [themeName],
                        onClick: handleThemeMenuClick,
                        style: { overflowY: 'auto', maxHeight: 400 },
                    }}
                />
            )}
        >
            <DropdownButton
                block
                style={{
                    justifyContent: 'flex-start',
                }}
            >
                <Row style={{ width: '100%' }}>
                    <Col span={12} style={{ textAlign: 'left' }}>
                        {activeTheme ? renderThemeLabel(activeTheme) : themeName}
                    </Col>
                </Row>
            </DropdownButton>
        </Dropdown>
    );
};

export default ThemeDropdown;
